<script setup lang="ts">
import type { HeaderSettings, LoginMenuSettings } from '@convatec/180medical-website/types/app'
import type { MenuItem } from '@convatec/180medical-website/types/navigation'
import type { DropdownMenuItem } from '@nuxt/ui'

const { items, loginMenuSettings, headerSettings } = defineProps<{
  items: MenuItem[]
  loginMenuSettings: LoginMenuSettings
  headerSettings: HeaderSettings
}>()

const searchTerm = ref('')
const appConfig = useAppConfig()
const uInputRef = ref(null)
const route = useRoute()

const links: DropdownMenuItem[] = []
loginMenuSettings.links?.forEach((l) => {
  links.push({
    label: l.text,
    icon: appConfig.ui.icons[l.iconName || ''],
    target: l.target,
    to: l.url,
  })
})

const activeMenu = ref()
function currentMenu(item: string) {
  activeMenu.value = item
}

const loginItems = ref<DropdownMenuItem[]>(links)

const formatPhone = (phone: string) => phone.replace(/\D/g, '')

const openSlideOver = ref(false)

function closeMenu() {
  activeMenu.value = null
  openSlideOver.value = false
}

function search() {
  if (!searchTerm?.value?.trim()) {
    return
  }

  if (route.query.searchTerm !== searchTerm.value.trim()) {
    closeMenu()
    navigateTo({
      path: headerSettings?.searchUrl,
      query: {
        ...route.query,
        page: 1,
        searchTerm: searchTerm.value.trim(),
      },
    })
  }
}

function clearSearch() {
  searchTerm.value = ''
  nextTick(() => {
    (uInputRef?.value as any)?.inputRef?.focus()
  })
}
</script>

<template>
  <div class="bg-white card-shadow w-full h-20 py-3">
    <div class="container-fluid">
      <div class="flex justify-end items-center gap-4">
        <UDropdownMenu
          :items="loginItems"
          :content="{
            align: 'end',
            side: 'bottom',
            sideOffset: 10,
          }"
          :ui="{
            content: 'w-full p-4 text-sm rounded-xl bg-white card-shadow',
            item: 'text-sm text-primary font-semibold underline',
            itemLeadingIcon: 'text-primary size-6',
            itemLabelExternalIcon: 'hidden',
          }"
          size="xl"
        >
          <button class="mobile-btn cursor-pointer">
            <span class="btn-icon--log-in" />
            <span>{{ loginMenuSettings.buttonText }}</span>
          </button>
        </UDropdownMenu>

        <USlideover
          :open="openSlideOver"
          title="180 Medical"
          :close="{
            color: 'primary',
            variant: 'outline',
            class: 'rounded-full min-w-auto border-0',
          }"
          @update:open="openSlideOver = $event"
        >
          <button
            class="mobile-btn cursor-pointer"
            @click="openSlideOver = true"
          >
            <span class="btn-icon--menu" />
            {{ headerSettings.menuLabel || 'Menu' }}
          </button>
          <template #body>
            <UInput
              ref="uInputRef"
              v-model="searchTerm"
              aria-label="Search"
              :placeholder="headerSettings.searchPlaceholderText || ''"
              size="lg"
              class="h-[40px] rounded-xl bg-white border-med-deep-navy border-2 shadow-0 mt-2.5 mb-6"
              variant="none"
              :ui="{
                base: 'shadow-none',
              }"
              @keyup.enter="search"
            >
              <template
                #trailing
              >
                <UButton
                  v-if="searchTerm?.length > 0"
                  class="cursor-pointer"
                  variant="ghost"
                  size="lg"
                  :icon="appConfig.ui.icons.close"
                  aria-label="Clear search"
                  @click="clearSearch()"
                />
                <UButton
                  class="cursor-pointer"
                  variant="ghost"
                  size="lg"
                  :icon="appConfig.ui.icons.search"
                  aria-label="Search"
                  @click="search()"
                />
              </template>
            </UInput>
            <ul class="mobile-nav">
              <li
                v-for="(parent) in items"
                :key="parent.contentGuid"
              >
                <UButton
                  color="primary"
                  variant="link"
                  class="mobile-nav__item with-card cursor-pointer"
                  @click="currentMenu(parent.title)"
                >
                  {{ parent.title }}
                </UButton>
                <div
                  class="mobile-nav__card"
                  :class="{ active: activeMenu === parent.title }"
                  :inert="activeMenu !== parent.title"
                >
                  <UButton
                    color="primary"
                    variant="link"
                    class="mobile-nav__back cursor-pointer"
                    @click="activeMenu = null"
                  >
                    <img
                      src="@/assets/images/icon-arrow-back.png"
                      alt="Back"
                      width="23"
                      height="16"
                      loading="lazy"
                    >
                    {{ headerSettings.menuBackLabel || "Back to menu" }}
                  </UButton>

                  <div class="mobile-nav__heading">
                    {{ parent.title }}
                  </div>
                  <template
                    v-for="(section, j) in parent.children"
                    :key="j"
                  >
                    <p class="mega-column__heading">
                      {{ section.category }}
                    </p>
                    <ul class="mega-column__nav-list">
                      <li
                        v-for="child in section.children"
                        :key="child.contentGuid"
                      >
                        <NuxtLink
                          :to="useCmsUrl(child.url)"
                          @click="closeMenu"
                        >
                          {{ child.title }}
                        </NuxtLink>
                      </li>
                    </ul>
                  </template>
                  <div
                    v-if="parent.promo"
                    class="mega-card"
                    :class="[{ 'mega-card--light-aqua': parent.promo.backgroundColor === 'aqua' }]"
                  >
                    <div v-if="parent.promo.variant !== 'subscription'">
                      <NuxtImg
                        provider="fastlyAprimo"
                        :src="parent.promo.image?.imageUrl"
                        :alt="parent.promo.image?.imageAltText"
                        class="mega-card__image"
                        loading="lazy"
                        :width="parent.promo.image?.width ?? 466"
                        :height="parent.promo.image?.height ?? 218"
                      />

                      <div class="mega-card__body">
                        <p class="mega-card__heading">
                          {{ parent.promo.title }}
                        </p>
                        <p
                          v-if="!parent.promo.linkSubtitle?.url"
                          class="mega-card__sub-head"
                        >
                          {{ parent.promo.subtitle }}
                        </p>
                        <p
                          v-if="parent.promo.linkSubtitle?.url"
                          class="mega-card__sub-head"
                        >
                          <NuxtLink
                            class="underline"
                            :to="useCmsUrl(parent.promo.linkSubtitle.url)"
                            @click="closeMenu"
                          >
                            {{ parent.promo.linkSubtitle?.title }}
                          </NuxtLink>
                        </p>
                        <p>{{ parent.promo.description }}</p>
                        <p>
                          <UButton
                            color="primary"
                            variant="solid"
                            size="lg"
                            :to="useCmsUrl(parent.promo.linkButton?.url)"
                            :locale="false"
                            :label="parent.promo.linkButton?.title"
                            @click="closeMenu"
                          />
                        </p>
                      </div>
                    </div>
                    <div v-if="parent.promo.variant === 'subscription'">
                      <LazyBlockSubscriptionBlock
                        :promo="parent.promo"
                        :close-menu="closeMenu"
                      />
                    </div>
                  </div>
                </div>
              </li>
            </ul>
            <div class="offcanvas__utility-nav prose">
              <p class="flex items-center gap-1 justify-center">
                <UIcon
                  :name="appConfig.ui.icons.phonefill"
                  class="shrink-0 inline-block text-[var(--color-med-deep-navy)] size-[24px]"
                  aria-hidden="true"
                />
                {{ headerSettings?.contactNumberLabel || '' }} <a
                  :href="`tel:${formatPhone(headerSettings?.contactNumber || '')}`"
                >{{ headerSettings?.contactNumber || '' }}</a>
              </p>
              <p>
                <UButton
                  color="info"
                  variant="solid"
                  class="w-full text-primary justify-center"
                  :to="useCmsUrl(headerSettings?.linkButton?.url || '')"
                  :locale="false"
                  @click="closeMenu"
                >
                  {{ headerSettings?.linkButton?.text || '' }}
                </UButton>
              </p>
            </div>
          </template>
        </USlideover>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
